import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import { getCurrentUser, login, getAndClearRedirect, type UserInfo } from './plugins/auth'
import { installErrorReporter } from './composables/useErrorReporter'

import '@mdi/font/css/materialdesignicons.css'

// Check URL for auth code (cross-origin dev flow)
const urlParams = new URLSearchParams(window.location.search)
const authCode = urlParams.get('auth_code')

if (authCode) {
  // Exchange auth code for session cookie
  fetch(`/api/v1/auth/exchange?code=${authCode}`, { credentials: 'include' })
    .then((res) => {
      if (res.ok) {
        // Remove auth_code from URL and reload
        const cleanUrl = new URL(window.location.href)
        cleanUrl.searchParams.delete('auth_code')
        window.location.href = cleanUrl.toString()
      } else {
        throw new Error('Failed to exchange auth code')
      }
    })
    .catch((err) => {
      console.error('Auth code exchange failed:', err)
      // Remove auth_code and try normal flow
      const cleanUrl = new URL(window.location.href)
      cleanUrl.searchParams.delete('auth_code')
      window.location.href = cleanUrl.toString()
    })
} else {
  // Check URL for auth errors
  const authError = urlParams.get('error')

  if (authError) {
    // Map error codes to user-friendly messages
    const errorMessages: Record<string, { title: string; message: string }> = {
      invalid_state: {
        title: 'Сессия авторизации истекла',
        message: 'Ссылка для входа устарела. Это может произойти, если вы долго не завершали вход или открыли несколько вкладок.',
      },
      no_code: {
        title: 'Ошибка авторизации',
        message: 'Не получен код авторизации от сервера. Попробуйте войти снова.',
      },
      auth_failed: {
        title: 'Ошибка авторизации',
        message: 'Не удалось завершить авторизацию. Проверьте учётные данные и попробуйте снова.',
      },
      access_denied: {
        title: 'Доступ запрещён',
        message: 'Вы отменили вход или у вас нет доступа к этому приложению.',
      },
    }

    const errorInfo = errorMessages[authError] || {
      title: 'Ошибка авторизации',
      message: `Произошла ошибка: ${authError}`,
    }

    // Show error and provide retry option
    document.body.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: 'Roboto', sans-serif; background: #121212; color: #fff;">
        <div style="text-align: center; max-width: 400px; padding: 32px;">
          <div style="font-size: 64px; margin-bottom: 16px;">🔐</div>
          <h1 style="margin: 0 0 16px; font-size: 24px; font-weight: 500;">${errorInfo.title}</h1>
          <p style="margin: 0 0 24px; color: #aaa; line-height: 1.5;">${errorInfo.message}</p>
          <a href="/" style="display: inline-block; padding: 12px 24px; background: #1976d2; color: #fff; text-decoration: none; border-radius: 4px; font-weight: 500;">
            Войти снова
          </a>
        </div>
      </div>
    `
  } else {
  // Helper to mount the Vue app
  function mountApp(user: UserInfo | null) {
    const app = createApp(App)
    const pinia = createPinia()
    app.use(pinia)
    app.use(router)
    app.use(vuetify)
    if (user) {
      app.provide('currentUser', user)
    }
    installErrorReporter(app)
    if (user) {
      const redirectPath = getAndClearRedirect()
      if (redirectPath && redirectPath !== window.location.pathname) {
        router.replace(redirectPath)
      }
    }
    app.mount('#app')
  }

  // Public routes (e.g. /public/submit/*) — mount without authentication
  const isPublicRoute = window.location.pathname.startsWith('/public/')
  if (isPublicRoute) {
    mountApp(null)
  } else {
    // Check authentication via BFF
    getCurrentUser().then((user) => {
      if (user) {
        mountApp(user)
      } else {
        login()
      }
    })
  }
  }
}
